/**
 * The review mirror (docs/50 §4.4.4, docs/42 §6, deck rulings r12, r13, c070, c127): how Google reviews get into
 * the platform and how an approved reply gets back out. This file is the interface only. The real adapter is our
 * own Business Profile API access, applied for at client one (r12) and built by WP-GOOGLE; until then a fixture
 * adapter (@ff/messaging) stands in, so the review feed, the reply drafts and the approval rules all run without a
 * Google call.
 *
 * A reply draft always carries the review's star rating. That closes the GLUE-D8 gap: the console's bulk approve
 * reads `rating` from the draft and never lets a 1 to 3 star reply through (c127), and a draft with no rating
 * counts as low.
 */
import { z } from 'zod';
import { IsoDateTime } from './common.ts';
import { LocationId, ReviewId } from './ids.ts';

/** A review as Google holds it. `googleName` is the stable key (`accounts/{a}/locations/{l}/reviews/{r}`). */
export const MirroredReview = z.object({
  googleName: z.string().min(3).max(300),
  rating: z.number().int().min(1).max(5),
  authorDisplay: z.string().max(120).nullable(),
  comment: z.string().max(4096).nullable(),
  createdTime: IsoDateTime,
  updatedTime: IsoDateTime.nullable(),
  /** The reply already on Google, ours or the owner's own. */
  reply: z.object({ text: z.string().max(4096), time: IsoDateTime }).nullable(),
});
export type MirroredReview = z.infer<typeof MirroredReview>;

/** Which profile to read: our location row and whatever Google identifiers it has so far. */
export const MirrorLocation = z.object({
  locationId: LocationId,
  /** `accounts/{a}/locations/{l}`, once the location is linked through the API. */
  gbpLocation: z.string().max(200).nullable(),
  placeId: z.string().max(300).nullable(),
});
export type MirrorLocation = z.infer<typeof MirrorLocation>;

/** A 1 to 3 star review: the reply goes to the client first, never automated, graduated or bulk approved (c127). */
export const CLIENT_FIRST_MAX_STARS = 3;

/**
 * What the drafting job stores in `outputs.draft_json` for a `review_reply`. `rating` is required: without it the
 * console can't tell a 5 star reply from a 1 star one.
 */
export const ReviewReplyDraft = z.object({
  reviewId: ReviewId,
  googleName: z.string().min(3).max(300),
  rating: z.number().int().min(1).max(5),
  reply: z.string().min(1).max(4096),
  /** True for 1 to 3 stars, or when the drafter flagged it: the client sees it before anything posts. */
  clientLook: z.boolean(),
  flags: z.array(z.string().max(200)).max(20),
  gates: z.array(z.object({ gate: z.string().max(60), ok: z.boolean(), message: z.string().max(300).nullable() })).max(20),
});
export type ReviewReplyDraft = z.infer<typeof ReviewReplyDraft>;

export function isClientFirstRating(rating: number): boolean {
  return !(Number.isInteger(rating) && rating > CLIENT_FIRST_MAX_STARS && rating <= 5);
}

/**
 * Pull reviews and post replies for one profile. Implementations: the fixture adapter today, the Business Profile
 * API later. Neither filters by rating: every review comes through, whatever it says (docs/43 §4.2).
 */
export interface ReviewMirror {
  readonly kind: 'fixture' | 'business_profile';
  /** Reviews created or changed since `since` (all of them when null), newest first. */
  pull(location: MirrorLocation, since: string | null): Promise<MirroredReview[]>;
  /** Post or replace our reply. `idempotencyKey` is stable per review and text, so a retry never posts twice. */
  postReply(location: MirrorLocation, googleName: string, text: string, idempotencyKey: string): Promise<{ postedAt: string }>;
}
